import { useState } from "react";

import { type NestedOptionType, type SelectedOptionType } from "./types";

export const useNestedDropdown = (
  options: NestedOptionType[],
  selectedOptions: SelectedOptionType[],
  setSelectedOptions: (items: SelectedOptionType[]) => void,
) => {
  const [selectedParent, setSelectedParent] = useState<string | null>(null);
  const [searchFilters, setSearchFilters] = useState<
    Record<string, string | null>
  >({});

  const isOptionSelected = (parentValue: string, optionValue: string) =>
    selectedOptions.some(
      (opt) => opt.parentValue === parentValue && opt.value === optionValue,
    );

  const getSelectedCountForParent = (parentValue: string) =>
    selectedOptions.filter((opt) => opt.parentValue === parentValue).length;

  const handleOptionSelect = (
    parentValue: string,
    option: NestedOptionType,
  ) => {
    let newSelectedOptions = [...selectedOptions];

    if (isOptionSelected(parentValue, option.value)) {
      newSelectedOptions = newSelectedOptions.filter(
        (opt) =>
          !(opt.parentValue === parentValue && opt.value === option.value),
      );
    } else {
      newSelectedOptions.push({
        parentValue,
        value: option.value,
        label: option.label,
      });
    }

    setSelectedOptions(newSelectedOptions);
  };

  const handleParentSelect = (option: NestedOptionType, checked: boolean) => {
    const newSelectedOptions = [
      ...selectedOptions.filter((opt) => opt.parentValue !== option.value),
    ];

    if (checked && option.subOptions) {
      option.subOptions.forEach((subOpt) => {
        newSelectedOptions.push({
          parentValue: option.value,
          value: subOpt.value,
          label: subOpt.label,
        });
      });
    }

    setSelectedOptions(newSelectedOptions);
  };

  const filterSubOptions = (
    parentOption: NestedOptionType,
  ): NestedOptionType[] | undefined => {
    if (!parentOption.subOptions) return undefined;

    return parentOption.subOptions.filter((subOption) => {
      const searchValue =
        searchFilters[parentOption.value]?.toLowerCase() || "";

      return (
        !searchValue || subOption.label.toLowerCase().includes(searchValue)
      );
    });
  };

  return {
    searchFilters,
    setSearchFilters,
    isOptionSelected,
    getSelectedCountForParent,
    handleOptionSelect,
    handleParentSelect,
    filterSubOptions,
    selectedParent,
    setSelectedParent,
  };
};
